import React, { useState, useEffect, useMemo, useRef } from 'react';
import { startOfToday, parseISO } from 'date-fns';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import {
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Edit as EditIcon,
  FirstPage,
  LastPage,
  Search as SearchIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import { useUser } from 'components/RDAuthContext';
import { useLocationAdmin } from '../context/LocationAdminContext';
import { useHierarchyStructure } from '../hooks/useLocationAdminData';
import LocationDetailPanel from '../components/LocationDetailPanel';
import AddLocationModal from '../components/AddLocationModal';
import { locationService } from '../services/domain'; // UPDATED: Using locationService

function LocationsTab() {
  const { user } = useUser();
  const renderStartTime = useRef(performance.now());
  const renderCount = useRef(0);
  
  // Get data from context
  const { 
    locations: contextLocations, 
    orgUnits: contextOrgUnits,
    isLoading,
    refetch 
  } = useLocationAdmin();
  
  // Get hierarchy data (lazy loaded)
  const { data: hierarchyData } = useHierarchyStructure();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [orgLevel1Filter, setOrgLevel1Filter] = useState('all');
  const [showActiveOnly, setShowActiveOnly] = useState(true); // Default to showing active only
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState([
    { id: 'locationName', desc: false } // Always sort by name alphabetically
  ]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [locationDetailsCache, setLocationDetailsCache] = useState({}); // Cache for full location details
  const [addModalOpen, setAddModalOpen] = useState(false);
  
  // Use context data
  const [locations, setLocations] = useState([]);
  const orgUnits = contextOrgUnits || [];
  const hierarchyDefinition = hierarchyData?.structure || [];
  const loading = isLoading;
  const onRefresh = refetch;
  
  // Track renders
  useEffect(() => {
    renderCount.current += 1;
    const renderTime = performance.now() - renderStartTime.current;

    // Log data sizes on first render
    if (renderCount.current === 1) {
    }
  });
  
  // Get orgLevel_1 label from hierarchy definition
  const orgLevel1Label = useMemo(() => {
    if (hierarchyDefinition && Array.isArray(hierarchyDefinition)) {
      const level1 = hierarchyDefinition.find(level => 
        level.levelId === 'ORGLEVEL_1' && level.isActive
      );
      return level1?.label || 'Market';
    }
    return 'Market';
  }, [hierarchyDefinition]);
  
  // Get unique orgLevel_1 units for filter - build from OrgUnits data
  const orgLevel1Options = useMemo(() => {
    const options = [];
    
    // Build options from orgUnits since locations only store the ID
    if (orgUnits && orgUnits.length > 0) {
      // Debug: log first unit to see structure
      if (orgUnits.length > 0) {
      }
      
      orgUnits.forEach(unit => {
        // Check if it's an OrgLevel_1 unit
        // The level field contains the level ID string like "ORGLEVEL_1"
        if (unit.level === 'ORGLEVEL_1') {
          options.push({
            id: unit.id || unit.orgUnitId,
            name: unit.name
          });
        }
      });
    }
    
    // Sort alphabetically by name
    options.sort((a, b) => a.name.localeCompare(b.name));

    return options;
  }, [orgUnits]);

  // Process locations when context data changes
  useEffect(() => {
    if (contextLocations && contextLocations.length > 0) {
      // Process locations to add display data
      const processedLocations = contextLocations.map(loc => ({
        ...loc,
        locationName: loc.name || loc.locationName,
        locationNumber: loc.locationNum || loc.locationNumber,
        city: loc.address?.city || loc.city,
        state: loc.address?.state || loc.state,
        ownershipType: loc.ownershipType || '-',
        format: loc.format || 'Traditional'
      }));
      
      setLocations(processedLocations);
    }
  }, [contextLocations]);
  
  // Clear cache when component unmounts or user changes
  useEffect(() => {
    return () => {
      setLocationDetailsCache({});
    };
  }, [user?.client]);
  
  // Handle location selection - fetch full details on demand with caching
  const handleLocationClick = async (location) => {
    // If clicking the same location, just open the panel
    if (selectedLocation?.id === location.id) {
      setDetailPanelOpen(true);
      return;
    }
    
    // Check if we have cached details for this location
    if (locationDetailsCache[location.id]) {
      setSelectedLocation(locationDetailsCache[location.id]);
      setDetailPanelOpen(true);
      return;
    }
    
    // Set basic info immediately for quick response
    setSelectedLocation(location);
    setDetailPanelOpen(true);
    setLoadingDetails(true);
    
    // Start performance timing for detail fetch
    const fetchStart = performance.now();
    
    try {
      // Fetch full details for the selected location
      const fullDetails = await locationService.getLocationFullDetails(
        user?.client,
        location.id
      );
      
      const fetchTime = performance.now() - fetchStart;

      // Cache the full details for this session
      setLocationDetailsCache(prevCache => ({
        ...prevCache,
        [location.id]: fullDetails
      }));
      
      // Update with full details
      setSelectedLocation(fullDetails);

    } catch (error) {
      const fetchTime = performance.now() - fetchStart;
      console.error(`[PERF] Error fetching location details after ${fetchTime.toFixed(2)}ms:`, error);
      // Keep basic info if details fetch fails
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Handle adding a new location
  const handleAddLocation = async (locationData) => {
    try {
      const newLocation = await locationService.createLocation(
        user?.clientId || user?.client,
        locationData,
        user.userName || user.id,
        `${user.firstName} ${user.lastName}` || user.userName
      );
      
      // Refresh the locations list
      if (onRefresh) {
        onRefresh();
      }
      
      // Close the modal
      setAddModalOpen(false);
      
      // Optionally, select the newly created location
      if (newLocation) {
        handleLocationClick(newLocation);
      }
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  };
  
  const handleLocationUpdate = (updatedLocation) => {
    // Update the location in the list
    setLocations(prev => prev.map(loc => 
      loc.id === updatedLocation.id ? updatedLocation : loc
    ));
    
    // Update the cache with the new data
    setLocationDetailsCache(prevCache => ({
      ...prevCache,
      [updatedLocation.id]: updatedLocation
    }));
    
    setSelectedLocation(updatedLocation);
    
    // Trigger a refetch to update Recent Changes and other data
    // This ensures the Overview tab and History tab get fresh data
    if (refetch) {
      refetch();
    }
  };

  // Define fixed columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'locationName',
        header: 'Location Name',
        cell: ({ row }) => (
          <Box display="flex" alignItems="center">
            <StoreIcon sx={{ mr: 0.75, color: '#7C3AED', fontSize: 18 }} />
            <Box>
              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8125rem', lineHeight: 1.2 }}>
                {row.original.locationName}
              </Typography>
              {row.original.locationNumber && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                  #{row.original.locationNumber}
                </Typography>
              )}
            </Box>
          </Box>
        ),
        size: 250,
      },
      {
        accessorKey: 'orgLevel_1_name',
        header: orgLevel1Label,
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
            {getValue() || 'Unassigned'}
          </Typography>
        ),
        size: 150,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status || 'Active';
          
          // Determine chip color based on status
          let chipColor = 'success';
          if (status === 'Closed') {
            chipColor = 'error';
          } else if (status === 'Closing') {
            chipColor = 'warning';
          }
          
          return (
            <Chip
              label={status}
              size="small"
              color={chipColor}
              variant="outlined"
            />
          );
        },
        size: 100,
      },
      {
        accessorKey: 'city',
        header: 'City',
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
            {getValue() || '-'}
          </Typography>
        ),
        size: 120,
      },
      {
        accessorKey: 'state',
        header: 'State',
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
            {getValue() || '-'}
          </Typography>
        ),
        size: 80,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Tooltip title="Edit Location">
            <IconButton size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
        size: 80,
      },
    ],
    [orgLevel1Label]
  );

  // Filter locations based on search term, orgLevel_1 filter, and active status
  const filteredLocations = useMemo(() => {
    let filtered = locations;
    
    // Apply active filter first
    if (showActiveOnly) {
      // A location is active if it doesn't have a closeDate or the closeDate is in the future
      const today = startOfToday();
      
      filtered = filtered.filter(loc => {
        if (!loc.closeDate) return true; // No close date means active
        
        try {
          const closeDate = parseISO(loc.closeDate);
          return closeDate > today; // Future close date means still active
        } catch (e) {
          // If date parsing fails, assume active
          return true;
        }
      });
    }
    
    // Apply orgLevel_1 filter
    if (orgLevel1Filter && orgLevel1Filter !== 'all') {
      filtered = filtered.filter(loc => loc.orgLevel_1 === orgLevel1Filter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(loc => {
        // Search in common fields
        if (loc.locationName?.toLowerCase().includes(lowerSearch)) return true;
        if (loc.locationNumber?.toLowerCase().includes(lowerSearch)) return true;
        if (loc.city?.toLowerCase().includes(lowerSearch)) return true;
        if (loc.state?.toLowerCase().includes(lowerSearch)) return true;
        if (loc.ownershipType?.toLowerCase().includes(lowerSearch)) return true;
        
        // Search in org level names
        if (loc.orgLevel_1_name?.toLowerCase().includes(lowerSearch)) return true;
        if (loc.orgLevel_2_name?.toLowerCase().includes(lowerSearch)) return true;
        if (loc.orgLevel_3_name?.toLowerCase().includes(lowerSearch)) return true;
        
        return false;
      });
    }
    
    return filtered;
  }, [locations, searchTerm, orgLevel1Filter, showActiveOnly]);

  // Create table instance
  const table = useReactTable({
    data: filteredLocations,
    columns,
    state: {
      rowSelection,
      sorting,
      pagination,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const renderTable = (table) => (
    <>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    style={{
                      width: header.getSize(),
                      padding: '8px 12px',
                      textAlign: 'left',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      color: '#424242'
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <tr 
                  key={row.id}
                  onClick={() => handleLocationClick(row.original)}
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: selectedLocation?.id === row.original.id ? '#e3f2fd' : row.getIsSelected() ? '#f5f5f5' : 'white',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!row.getIsSelected() && selectedLocation?.id !== row.original.id) {
                      e.currentTarget.style.backgroundColor = '#fafafa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!row.getIsSelected() && selectedLocation?.id !== row.original.id) {
                      e.currentTarget.style.backgroundColor = selectedLocation?.id === row.original.id ? '#e3f2fd' : 'white';
                    }
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                        padding: '6px 12px',
                        fontSize: '0.8125rem'
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td 
                  colSpan={table.getAllColumns().length}
                  style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: '#666'
                  }}
                >
                  No locations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      
      {/* Pagination Controls */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mt: 2,
          pt: 2,
          borderTop: '1px solid #e0e0e0'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <Select
            size="small"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            sx={{ minWidth: 70 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
          <Typography variant="body2" color="text.secondary">
            {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
            -
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{' '}
            of {table.getFilteredRowModel().rows.length} rows
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <FirstPage />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 2 }}>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </Typography>
          <IconButton
            size="small"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <LastPage />
          </IconButton>
        </Box>
      </Box>
    </>
  );

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" fontWeight="600" gutterBottom>
                Location Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedLocation 
                  ? `Viewing details for ${selectedLocation.locationName}`
                  : (() => {
                      const parts = [];
                      
                      // Show active/all status
                      if (showActiveOnly) {
                        parts.push('Active locations');
                      } else {
                        parts.push('All locations');
                      }
                      
                      // Show org filter if applied
                      if (orgLevel1Filter && orgLevel1Filter !== 'all') {
                        const selectedOrg = orgLevel1Options.find(opt => opt.id === orgLevel1Filter);
                        if (selectedOrg) {
                          parts.push(`in ${selectedOrg.name}`);
                        }
                      }
                      
                      // Show search if applied
                      if (searchTerm) {
                        parts.push(`matching "${searchTerm}"`);
                      }
                      
                      // Show count
                      parts.push(`(${filteredLocations.length} ${filteredLocations.length === 1 ? 'location' : 'locations'})`);
                      
                      return parts.join(' ');
                    })()
                }
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              {selectedLocation && (
                <AdminButton
                  variant="outlined"
                  onClick={() => setDetailPanelOpen(true)}
                  sx={{ display: detailPanelOpen ? 'none' : 'flex' }}
                >
                  View Details
                </AdminButton>
              )}
              <AdminButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddModalOpen(true)}
              >
                Add Location
              </AdminButton>
            </Box>
          </Box>

          <Box display="flex" gap={2} mb={3} alignItems="center">
            <TextField
              placeholder="Search locations by name, number, or city..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Reset to first page when searching
                setPagination(prev => ({ ...prev, pageIndex: 0 }));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="org-level-1-filter" sx={{ top: '-7px' }}>{orgLevel1Label} Filter</InputLabel>
              <Select
                labelId="org-level-1-filter"
                value={orgLevel1Filter}
                onChange={(e) => {
                  setOrgLevel1Filter(e.target.value);
                  // Reset to first page when filtering
                  setPagination(prev => ({ ...prev, pageIndex: 0 }));
                }}
                label={`${orgLevel1Label} Filter`}
                sx={{ height: '40px' }}
              >
                <MenuItem value="all">All {orgLevel1Label}s</MenuItem>
                {orgLevel1Options.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                  size="small"
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <Typography variant="caption" fontWeight="medium">
                  Active Only
                </Typography>
              }
              labelPlacement="start"
              sx={{ m: 0, ml: 1 }}
            />
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography variant="body2" color="text.secondary">
                Loading locations...
              </Typography>
            </Box>
          ) : (
            <>
              {filteredLocations.length > 0 && searchTerm && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Found {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''} matching "{searchTerm}"
                  </Typography>
                </Box>
              )}
              {renderTable(table)}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Location Detail Panel */}
      <LocationDetailPanel
        location={selectedLocation}
        open={detailPanelOpen}
        onClose={() => {
          setDetailPanelOpen(false);
          setSelectedLocation(null);  // Clear selection when panel closes
        }}
        onUpdate={handleLocationUpdate}
        loading={loadingDetails}
        hierarchyDefinition={hierarchyDefinition}
        orgUnits={orgUnits}
      />
      
      {/* Add Location Modal */}
      <AddLocationModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddLocation}
        orgUnits={orgUnits}
        hierarchyDefinition={hierarchyDefinition}
      />
    </Box>
  );
}

export default LocationsTab;